<script setup lang="ts">
import {computed, inject} from 'vue'
import {Badge} from '@hantera/design-system'
import {useDialogs, useLocalization} from '@hantera/portal-app'
import type {useClientStore} from '@/stores/iam/client-store'
import ClientSecretModal from './ClientSecretModal.vue'
import GenerateSecretDialog from './GenerateSecretDialog.vue'

const t = useLocalization('iam.clients')
const dialogs = useDialogs()
const store = inject<ReturnType<typeof useClientStore>>('clientStore')!

const client = computed(() => store.client)
const secrets = computed(() => store.secrets)
const canWrite = computed(() => store.canWrite)

function formatDateTime(date: string): string {
  return new Date(date).toLocaleString()
}

async function handleGenerateSecret() {
  const dialogController = dialogs.openModal(
    t('secrets.modal.generateTitle'),
    GenerateSecretDialog,
    false,
    {
      onGenerate: async (description: string, expiresAt: Date) => {
        dialogController.close()

        const result = await store.generateSecret(description, expiresAt)
        if (result) {
          const secretController = dialogs.openModal(
            t('secrets.modal.title'),
            ClientSecretModal,
            false,
            {
              secret: result.secret,
              onClose: () => secretController.close()
            }
          )
        }
      },
      onCancel: () => {
        dialogController.close()
      }
    }
  )
}

async function handleRevokeSecret(secretId: string) {
  await store.revokeSecret(secretId)
}

const activeSecrets = computed(() =>
  secrets.value.filter(s => !s.isRevoked && !s.isExpired)
)

const revokedSecrets = computed(() =>
  secrets.value.filter(s => s.isRevoked || s.isExpired)
)
</script>

<template>
  <div v-if="client" class="secrets-tab">
    <div class="tab-header">
      <h3>{{ t('secrets.title') }}</h3>
      <p class="help-text">{{ t('secrets.helpText') }}</p>
      <button v-if="canWrite" @click="handleGenerateSecret" class="btn-primary">
        {{ t('actions.generateSecret') }}
      </button>
    </div>

    <div v-if="activeSecrets.length === 0 && revokedSecrets.length === 0" class="empty-state">
      <p>{{ t('secrets.empty') }}</p>
    </div>

    <div v-if="activeSecrets.length > 0" class="secrets-section">
      <h4>{{ t('secrets.activeSectionTitle') }}</h4>
      <div class="active-secrets-list">
        <div v-for="secret in activeSecrets" :key="secret.id" class="secret-item active-secret box">
          <div class="secret-info">
            <div class="secret-content">
              <div class="secret-badges">
                <Badge variant="success">{{ t('secrets.active') }}</Badge>
              </div>
              <div class="secret-id monospace">{{ secret.id }}</div>
              <div v-if="secret.description" class="secret-description">{{ secret.description }}</div>
              <div class="secret-meta">{{ t('secrets.table.expires') }}: {{ formatDateTime(secret.expiresAt) }}</div>
            </div>
            <div v-if="canWrite" class="secret-actions">
              <button 
                @click="handleRevokeSecret(secret.id)" 
                class="btn-warning btn-small"
              >
                {{ t('actions.revoke') }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="revokedSecrets.length > 0" class="secrets-section">
      <h4>{{ t('secrets.revokedSectionTitle') }}</h4>
      <div class="revoked-secrets-list">
        <div v-for="secret in revokedSecrets" :key="secret.id" class="secret-item revoked-secret box">
          <div class="secret-info">
            <div class="secret-badges">
              <Badge variant="neutral">{{ secret.isRevoked ? t('secrets.revoked') : t('secrets.expired') }}</Badge>
            </div>
            <div class="secret-id monospace">{{ secret.id }}</div>
            <div v-if="secret.description" class="secret-description">{{ secret.description }}</div>
            <div class="secret-meta">{{ t('secrets.table.expires') }}: {{ formatDateTime(secret.expiresAt) }}</div>
            <div v-if="secret.revokedAt" class="secret-meta">
              {{ t('secrets.revokedAt') }}: {{ formatDateTime(secret.revokedAt) }}
            </div>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<style scoped>
.secrets-tab {
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.tab-header {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.tab-header h3 {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
}

.help-text {
  color: var(--color-text-secondary);
  font-size: 0.875rem;
  margin: 0;
}

.empty-state {
  text-align: center;
  padding: 3rem;
  color: var(--color-text-secondary);
}

.monospace {
  font-family: 'Courier New', monospace;
  font-size: 0.875rem;
}

.secrets-section {
  margin-top: 1.5rem;
}

.active-secrets-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.secrets-section h4 {
  margin: 0 0 1rem 0;
  font-size: 1rem;
  font-weight: 600;
  color: var(--color-text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.revoked-secrets-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.secret-item {
  padding: 1rem;
}

.secret-item.active-secret {
  opacity: 1;
}

.secret-item.revoked-secret {
  opacity: 0.7;
}

.secret-info {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 1rem;
  align-items: start;
}

.secret-content {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.secret-badges {
  display: flex;
  gap: 0.5rem;
}

.secret-id {
  font-size: 0.875rem;
}

.secret-description {
  font-size: 0.875rem;
  color: var(--color-text-secondary);
}

.secret-meta {
  font-size: 0.875rem;
  color: var(--color-text-secondary);
}

.secret-actions {
  display: flex;
  align-items: flex-start;
}

@container (max-width: 400px) {
  .secret-info {
    grid-template-columns: 1fr;
  }

  .secret-actions {
    margin-top: 5px;
  }
}
</style>
